import { ReactNode } from "react";
import { format } from "date-fns";
import {
  Briefcase,
  ExternalLink,
  CalendarDays,
  Clock,
  CalendarCheck2,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";

// Types
import { TarefaDTO } from "@/types/tarefa.type";
import { PessoaDTO } from "@/types/pessoa";
import { PermissaoEnum, TarefaStatus, TarefaTipo, TarefaVisibilidade } from "@workflow/types";

// UI Components
import { Card, CardContent } from "@/components/ui/card";
import { buttonVariants } from "@/components/ui/button";
import { PermissionWrapper } from "@/components/permissao/PermissionWrapper";
import { TarefaStatusBadge } from "@/components/tarefa/TarefaStatusBadge";
import { TarefaTipoBadge } from "@/components/tarefa/TarefaTipoBadge";
import { Person } from "@/components/shared/person";

// Utils
import { resolveResourceLink } from "@/lib/navigation-helpers";
import { cn } from "@/lib/utils";
import { TarefaDataPrazoCard } from "./TarefaDataPrazoCard";
import { TarefaVisibilidadeCard } from "./TarefaVisibilidadeCard";

interface TarefaSidebarInfoProps {
  tarefa: TarefaDTO;
  permissions: {
    isAdmin: boolean;
    isCriador: boolean;
    isResponsavel: boolean;
  };
  isFechada: boolean;
  isUpdateLoading: boolean;
  /** Pode editar a visibilidade (tarefa genérica, aberta, criador/admin). */
  canEditVisibilidade: boolean;
  isUpdateVisibilidadeLoading: boolean;
  onNavigateToJob: (jobId: number) => void;
  onUpdateDataPrazo: (dataPrazo: Date | null) => void;
  onUpdateVisibilidade: (visibilidade: TarefaVisibilidade) => void;
}

const formatDateTime = (value: Date | string) => format(new Date(value), "dd/MM/yyyy HH:mm");

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

/** Cabeçalho de seção: agrupa os blocos da sidebar (sentence-case, discreto). */
function SectionTitle({ children }: { children: ReactNode }) {
  return <h3 className="text-xs font-semibold text-foreground/70">{children}</h3>;
}

/** Rótulo padrão da sidebar: minúsculo, caixa-alta, com ícone opcional. */
function FieldLabel({
  icon: Icon,
  className,
  children,
}: {
  icon?: LucideIcon;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground",
        className,
      )}
    >
      {Icon && <Icon className="h-3.5 w-3.5" />}
      {children}
    </span>
  );
}

/** Par rótulo + data formatada (Criação, Últ. alteração, Conclusão). */
function DateField({
  icon,
  label,
  value,
  className,
  labelClassName,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  className?: string;
  labelClassName?: string;
}) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <FieldLabel icon={icon} className={labelClassName}>
        {label}
      </FieldLabel>
      <p className="text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}

function ActorRow({
  label,
  person,
  link,
  showLink,
  linkLabel,
}: {
  label: string;
  person: PessoaDTO;
  showLink: boolean;
  linkLabel: string;
  link?: string;
}) {
  return (
    <div className="group flex items-center justify-between gap-2 py-3 first:pt-0 last:pb-0">
      <Person.Root className="flex min-w-0 items-center gap-3" pessoa={person} asClickable>
        <Person.Avatar />
        <div className="flex min-w-0 flex-col">
          <FieldLabel className="mb-0.5">{label}</FieldLabel>
          <Person.Name className="truncate text-sm font-medium leading-none text-foreground" />
        </div>
      </Person.Root>

      {showLink && link && (
        <Link
          className={cn(
            buttonVariants({ variant: "outline", size: "sm" }),
            "h-7 shrink-0 px-2.5 text-[10px] font-bold uppercase tracking-wider transition-all duration-200",
            label === "Responsável"
              ? "border-primary/20 text-primary hover:border-primary/30 hover:bg-primary/10"
              : "text-muted-foreground hover:text-foreground",
          )}
          to={link}
        >
          <ExternalLink className="h-3.5 w-3.5" />{" "}
          <span className="hidden lg:ml-1.5 xl:inline">{linkLabel}</span>
        </Link>
      )}
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function TarefaSidebarInfo(props: TarefaSidebarInfoProps) {
  const {
    tarefa,
    permissions,
    isFechada,
    isUpdateLoading,
    canEditVisibilidade,
    isUpdateVisibilidadeLoading,
    onNavigateToJob,
    onUpdateDataPrazo,
    onUpdateVisibilidade,
  } = props;

  const linkAcaoBase = resolveResourceLink(tarefa.acao);
  // Para a tarefa de correção de faturamento, levamos o tarefaId no link de ação para
  // que a tela do faturamento reprovado possa concluir a tarefa ao refaturar.
  const linkAcao =
    linkAcaoBase && tarefa.tipo === TarefaTipo.CORRIGIR_FATURAMENTO
      ? `${linkAcaoBase}?tarefaId=${tarefa.id}`
      : linkAcaoBase;
  const linkSolicitante = resolveResourceLink(tarefa.origem);

  const canEditPrazo = (permissions.isAdmin || permissions.isCriador) && !isFechada;
  const isConcluido = tarefa.status === TarefaStatus.CONCLUIDO;
  const canSeeJob = permissions.isCriador || permissions.isAdmin;

  return (
    <Card className="overflow-hidden border-border/60 bg-card shadow-sm">
      <CardContent className="divide-y divide-border/40 p-0">
        {/* Visão geral: status, categoria e visibilidade */}
        <section className="space-y-4 bg-muted/10 p-5">
          <SectionTitle>Visão geral</SectionTitle>

          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <FieldLabel>Status</FieldLabel>
              <TarefaStatusBadge status={tarefa.status as TarefaStatus} />
            </div>
            <div className="space-y-2">
              <FieldLabel>Categoria</FieldLabel>
              <TarefaTipoBadge tipo={tarefa.tipo} />
            </div>
          </div>

          <TarefaVisibilidadeCard
            visibilidade={tarefa.visibilidade}
            canEdit={canEditVisibilidade}
            isLoading={isUpdateVisibilidadeLoading}
            onUpdate={onUpdateVisibilidade}
          />
        </section>

        {/* Datas: prazo, criação, alteração e conclusão */}
        <section className="space-y-5 p-5">
          <SectionTitle>Datas</SectionTitle>

          <TarefaDataPrazoCard
            dataPrazo={tarefa.dataPrazo}
            isFechada={isFechada}
            canEdit={canEditPrazo}
            isLoading={isUpdateLoading}
            onUpdate={onUpdateDataPrazo}
          />

          <div className="grid grid-cols-2 gap-4">
            <DateField
              icon={CalendarDays}
              label="Criação"
              value={formatDateTime(tarefa.criadoEm!)}
            />
            {tarefa.alteradoEm && (
              <DateField
                icon={Clock}
                label="Últ. alteração"
                value={formatDateTime(tarefa.alteradoEm)}
              />
            )}
          </div>

          {isConcluido && (
            <DateField
              icon={CalendarCheck2}
              label="Conclusão"
              value={tarefa.dataConclusao ? formatDateTime(tarefa.dataConclusao) : "-"}
              className="border-t border-border/30 pt-4"
              labelClassName="text-emerald-600 dark:text-emerald-500"
            />
          )}
        </section>

        {/* Pessoas e job: linhas com mesmo ritmo, separadas internamente */}
        <section className="space-y-3 bg-muted/5 p-5">
          <SectionTitle>Pessoas e job</SectionTitle>

          <div className="divide-y divide-border/50">
            {tarefa.job && (
              <PermissionWrapper permissions={[PermissaoEnum.JOB_FIND_ALL]} requireAll={false}>
                <button
                  type="button"
                  onClick={() => onNavigateToJob(tarefa.job!.id)}
                  className="group flex w-full items-center gap-3 py-3 text-left first:pt-0 last:pb-0"
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary shadow-sm transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                    <Briefcase className="h-3.5 w-3.5" />
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <FieldLabel className="mb-0.5">Job Relacionado</FieldLabel>
                    <span className="truncate text-sm font-medium leading-none text-foreground transition-colors group-hover:text-primary">
                      {tarefa.job.nome}
                    </span>
                  </span>
                </button>
              </PermissionWrapper>
            )}

            {tarefa.criadoPor && (
              <ActorRow
                label="Solicitante"
                person={tarefa.criadoPor}
                link={linkSolicitante}
                showLink={canSeeJob}
                linkLabel="Ver Origem"
              />
            )}

            {tarefa.atribuidoA && (
              <ActorRow
                label="Responsável"
                person={tarefa.atribuidoA}
                link={linkAcao}
                showLink={permissions.isResponsavel || permissions.isAdmin}
                linkLabel="Realizar Tarefa"
              />
            )}
          </div>
        </section>
      </CardContent>
    </Card>
  );
}
